// src/components/dashboard/candidate/ProfileSetupPage.tsx
// Full-page inline profile creation for logged-in candidates without a profile.
import React, { useState } from 'react';
import { useCandidateProfile } from '@/contexts/CandidateProfileContext';
import type { CandidateProfileCreatePayload } from '@/lib/models/candidateProfile';
import { ProfileStepsForm } from './ProfileStepsForm';
import { CelebrationModal } from './journey-steps/CelebrationModal';
import AppHeader from '@/components/layout/AppHeader';

const ProfileSetupPage: React.FC = () => {
    const { createUserProfile, refreshUserProfile } = useCandidateProfile();
    const [showCelebration, setShowCelebration] = useState(false);

    const handleComplete = async (payload: CandidateProfileCreatePayload) => {
        const result = await createUserProfile(payload);
        if (!result) {
            throw new Error('Kunde inte skapa profilen. Försök igen.');
        }
        setShowCelebration(true);
    };

    // The profile refresh is what swaps this page out for the dashboard, so it
    // runs when the user chooses to move on — not on a 2.5s timer that could
    // pull the screen away mid-read.
    const handleContinue = async () => {
        setShowCelebration(false);
        await refreshUserProfile();
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex flex-col">
            <AppHeader />

            {/* Main content */}
            <div className="flex-1 flex items-start justify-center px-4 py-8">
                <div className="w-full max-w-2xl">
                    {/* Info banner */}
                    <div className="mb-6 p-5 rounded-2xl bg-[#A4C3B2]/30 border border-[#A4C3B2]">
                        <h2 className="text-lg font-bold text-gray-900 mb-1">Skapa din kandidatprofil</h2>
                        <p className="text-sm text-gray-600">
                            Innan du kan komma igång behöver vi veta lite om dig. Dina svar hjälper oss matcha dig
                            med relevanta moduler, uppgifter och jobbmöjligheter. Det tar bara ett par minuter!
                        </p>
                    </div>

                    <ProfileStepsForm onComplete={handleComplete} />
                </div>
            </div>

            <CelebrationModal
                isOpen={showCelebration}
                variant="profile"
                onContinue={handleContinue}
            />
        </div>
    );
};

export default ProfileSetupPage;
